"use strict"

import React, { useEffect, useReducer, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"

import { displayRef } from "../../widgets/utils"
import { checkIfDuplicateExists } from "../../widgets/utils"
import { AddSourceTags } from "./ModalParts"
import AreYouSureModal from "../../widgets/AreYouSureModal"
import LoaderGif from "../../widgets/LoaderGif"

const initialState = {
  isLoading: false,
  newTags: [],
}

function formReducer(state, action) {
  switch (action.type) {
    case "row": {
      let newTags = Object.assign([], state.newTags)
      newTags.push({ tag: "", units: "", observation_type: "", warning: false })
      return {
        ...state,
        newTags: newTags,
      }
    }
    case "remove_row": {
      let newTags = Object.assign([], state.newTags)
      newTags.splice(action.index, 1)
      return {
        ...state,
        newTags: newTags,
      }
    }
    case "field": {
      let newTags = Object.assign([], state.newTags)
      let tag_info = { ...newTags[action.index] }
      tag_info[action.field] = action.value
      newTags[action.index] = tag_info

      return {
        ...state,
        newTags: newTags,
      }
    }
    case "clear": {
      return {
        ...state,
        ...initialState,
      }
    }
    case "submit": {
      return {
        ...state,
        isLoading: true,
      }
    }
    case "invalid-form": {
      return {
        ...state,
        isLoading: false,
      }
    }
  }
}

export default function AddSourceTagsModal(props) {
  const intl = useIntl()
  const [state, dispatch] = useReducer(formReducer, { ...initialState })
  const { isLoading, newTags } = state
  const [tags, setTags] = useState([])

  useEffect(() => {
    const registered_tags = props.source.tags.map((tag) => tag.tag)
    const universal_tags = props.queryTags.map((tag) => tag.name)
    const available_tags = universal_tags.filter((tag) => !registered_tags.includes(tag))
    const final_query_tags = props.queryTags.filter((tag) => available_tags.includes(tag.name))
    setTags(final_query_tags)
  }, [props.source.tags])

  const updateFormValue = (e, field, value, index, warning) => {
    e.preventDefault()
    dispatch({ type: "field", field: field, value: value, index: index })
    if (warning) {
      dispatch({ type: "field", field: "warning", value: false, index: index })
    }
  }

  const [data, setData] = useState(false)
  const [verifySubmitModal, setVerifySubmitModal] = useState(false)

  const validateForm = (e) => {
    displayRef("add_source_tags_gif", true)
    e.preventDefault()
    dispatch({ type: "submit" })

    let validSubmission = true
    const tagDuplicates = checkIfDuplicateExists(newTags.map((tag) => tag.tag))
    if (newTags.length > 0) {
      const fields = ["observation_type", "tag", "units"]
      newTags.forEach((tag, index) => {
        fields.forEach((field) => {
          if (
            tag[field] === "" ||
            !tag[field] ||
            (field === "tag" && tagDuplicates && newTags.filter((t) => t.tag === tag[field]).length > 1)
          ) {
            validSubmission = false
            dispatch({ type: "field", field: "warning", value: true, index: index })
          }
        })
      })
    } else {
      validSubmission = false
    }

    if (validSubmission) {
      setData(newTags)
      setVerifySubmitModal(true)
      displayRef("add_source_tags_modal", false)
    } else {
      displayRef("add_source_tags_gif", false)
      dispatch({ type: "invalid-form" })
    }
  }

  const generalFormSubmission = (e) => {
    if (e) {
      e.preventDefault()
    }

    setVerifySubmitModal(false)
    dispatch({ type: "invalid-form" })
    displayRef("add_source_tags_modal", true)
  }

  const submitForm = (e, data) => {
    generalFormSubmission(e)
    props.submitData(data, "add_source_tags_gif", props.source.id)
  }

  const cancelForm = (e) => {
    generalFormSubmission(e)
    displayRef("add_source_tags_gif", false)
  }

  return (
    <React.Fragment>
      {verifySubmitModal && (
        <AreYouSureModal
          show={verifySubmitModal}
          onHide={(e) => cancelForm(e)}
          data={data}
          modalBody={<AddSourceTags data={data} source={props.source} />}
          submitForm={submitForm}
          cancelForm={cancelForm}
        />
      )}
      <Modal
        size={"lg"}
        dialogClassName={"common-modal"}
        show={props.show}
        onHide={props.onHide}
        style={{ overflowX: "scroll", overflowY: "scroll" }}
        backdrop={"static"}
        id={"add_source_tags_modal"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div>
              <span className={"fa fa-check-square-o"} style={{ marginRight: "20px" }} />
              <FormattedMessage
                id={"navigation.source.registration"}
                description={"Source Registration Page Title"}
              />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoaderGif id={"add_source_tags_gif"} />
          {props.warning && (
            <h6 style={{ marginTop: "10px", marginBottom: "20px" }} className={"error-message"}>
              <FormattedMessage id={"general.server.error"} description={"Server Error Message"} />
              :
              <br />
              {props.warning}
            </h6>
          )}
          <div>
            <h6>
              <FormattedMessage
                id={"sourceregistration.register.new.source.tags"}
                description={"Register New Source Tags"}
              />
              : {props.source.name}
            </h6>
            <table className="table table-sm tbl_lanzatable">
              <thead>
                <tr>
                  <th className={"text-center"}>
                    <FormattedMessage
                      id={"general.observation.type"}
                      description={"New SourceTag Observation Type Column Header"}
                    />
                  </th>
                  <th className={"text-center"}>
                    <FormattedMessage id={"general.tag"} description={"New SourceTag Tag Column Header"} />
                  </th>
                  <th className={"text-center"}>
                    <FormattedMessage
                      id={"general.units"}
                      description={"New SourceTag Units Column Header"}
                    />
                  </th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {newTags.map((tag, idx) => {
                  const has_tag_mapped = tag.tag && tag.tag !== ""
                  const mapped_tags_list = newTags.map((t) => t.tag)
                  return (
                    <React.Fragment key={`new_tag_${idx}`}>
                      <tr
                        style={
                          tag.warning
                            ? { backgroundColor: "var(--lanzalake-red-light)" }
                            : { backgroundColor: "var(--lanzalake-white)" }
                        }
                      >
                        <td>
                          <select
                            className={"form-select form-select-sm"}
                            name={"observation_type"}
                            value={tag.observation_type}
                            onChange={(e) =>
                              updateFormValue(e, "observation_type", e.target.value, idx, tag.warning)
                            }
                          >
                            <option value="">
                              {intl.formatMessage({
                                id: "general.dropdown.please.select",
                              })}
                            </option>
                            {props.observation_types
                              .filter((ot) => ot !== "Computed")
                              .map((ot, idx) => {
                                return (
                                  <option key={idx} value={ot}>
                                    {ot}
                                  </option>
                                )
                              })}
                          </select>
                        </td>
                        <td>
                          <select
                            className={"form-select form-select-sm"}
                            name={"tag"}
                            value={tag.tag}
                            onChange={(e) => updateFormValue(e, "tag", e.target.value, idx, tag.warning)}
                          >
                            <option value="">
                              {intl.formatMessage({
                                id: "general.dropdown.please.select",
                              })}
                            </option>
                            {has_tag_mapped && <option value={tag.tag}>{tag.tag}</option>}
                            {tags
                              .filter((t) => !mapped_tags_list.includes(t.name))
                              .map((t, idx) => {
                                return (
                                  <option key={idx} value={t.name}>
                                    {t.name}
                                  </option>
                                )
                              })}
                          </select>
                        </td>
                        <td>
                          <select
                            className={"form-select form-select-sm"}
                            name={"units"}
                            value={tag.units}
                            onChange={(e) => updateFormValue(e, "units", e.target.value, idx, tag.warning)}
                          >
                            <option value="">
                              {intl.formatMessage({
                                id: "general.dropdown.please.select",
                              })}
                            </option>
                            {props.units.map((unit, idx) => {
                              return (
                                <option key={idx} value={unit.name}>
                                  {unit.name}
                                </option>
                              )
                            })}
                          </select>
                        </td>
                        <td>
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={(e) => {
                              e.preventDefault()
                              dispatch({ type: "remove_row", index: idx })
                            }}
                          >
                            <i className="fa fa-times text-danger" />
                          </Button>
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
            <Button
              variant={"outline-primary"}
              size={"sm"}
              onClick={(e) => {
                e.preventDefault()
                dispatch({ type: "row" })
              }}
            >
              <i className="fa fa-plus text-primary" />
              &nbsp;&nbsp;
              <span>
                <FormattedMessage id={"general.tag"} description={"Add tag button"} />
              </span>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ marginRight: "auto" }}
            type={"reset"}
            variant={"outline-primary"}
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault()
              dispatch({ type: "clear" })
              props.onHide()
            }}
          >
            <FormattedMessage id={"general.closeButton"} description={"Close Button For Form"} />
          </Button>
          <Button
            style={{ marginLeft: "auto" }}
            type={"reset"}
            variant={"danger"}
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault()
              dispatch({ type: "clear" })
            }}
          >
            <FormattedMessage id={"general.clearButton"} description={"Clear Button For Form"} />
          </Button>
          &nbsp;
          <Button type={"submit"} variant={"primary"} disabled={isLoading} onClick={(e) => validateForm(e)}>
            <FormattedMessage id={"general.submitButton"} description={"Submit Button For Form"} />
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}
